import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LANGS = ['en', 'ru', 'uz', 'tr', 'zh'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGS)[number];

/** Locale JSON shape: `{ "translation": { ...keys } }` */
type LocaleFile = {
    translation: Record<string, unknown>;
};

const loaders: Record<AppLanguage, () => Promise<{ default: LocaleFile }>> = {
    en: () => import('./translations/en.json'),
    ru: () => import('./translations/ru.json'),
    uz: () => import('./translations/uz.json'),
    tr: () => import('./translations/tr.json'),
    zh: () => import('./translations/zh.json')
};

const loadedLanguages = new Set<string>();

function resolveInitialLanguage(): AppLanguage {
    const saved = localStorage.getItem('lang');
    if (saved && SUPPORTED_LANGS.includes(saved as AppLanguage)) {
        return saved as AppLanguage;
    }
    return 'uz';
}

async function loadLocaleFile(lng: AppLanguage): Promise<LocaleFile> {
    const mod = await loaders[lng]();
    return mod.default;
}

async function loadLanguageBundle(lng: string): Promise<void> {
    const key = (SUPPORTED_LANGS.includes(lng as AppLanguage) ? lng : 'uz') as AppLanguage;
    if (loadedLanguages.has(key)) return;

    const localeFile = await loadLocaleFile(key);
    i18n.addResourceBundle(key, 'translation', localeFile.translation, true, true);
    loadedLanguages.add(key);
}

let initPromise: Promise<typeof i18n> | null = null;

/** Load only the active locale on startup; other locales load on demand. */
export function initI18n(): Promise<typeof i18n> {
    if (initPromise) return initPromise;

    initPromise = (async () => {
        const lng = resolveInitialLanguage();
        const localeFile = await loadLocaleFile(lng);

        if (!i18n.isInitialized) {
            // JSON files already use `{ translation: { ... } }` — do not wrap again.
            await i18n.use(initReactI18next).init({
                resources: { [lng]: localeFile },
                lng,
                fallbackLng: 'uz',
                interpolation: { escapeValue: false }
            });
            loadedLanguages.add(lng);
        }

        if (lng !== 'uz' && !loadedLanguages.has('uz')) {
            await loadLanguageBundle('uz');
        }

        return i18n;
    })();

    return initPromise;
}

/** Switch language — dynamically imports bundle if not yet loaded. */
export async function changeAppLanguage(lng: string): Promise<void> {
    await initI18n();
    await loadLanguageBundle(lng);
    await i18n.changeLanguage(lng);
    localStorage.setItem('lang', lng);
}

export default i18n;
